import {
  AgentStreamAccept,
  ChatMessageVO,
  ChatPollWithStatusVO,
  ChatSendRequest,
  DocumentRef,
  EntityAction,
  QuestionData,
  StreamError,
  StreamHandlers,
  _chatPollWithStatus,
  _chatResumeStream,
  _chatSendStream
} from '../services/aiAssistant';
import { extractQuestionData } from './messageMapper';
import { extractToolCalls, ThinkingToolCall } from './ThinkingCard';
import { MessageTokens } from './TokenTag';

/** 一轮对话失败时的统一兜底文案 */
export const TURN_FAIL_TEXT = '抱歉，请求发送失败，请稍后重试。';

/** 轮询兜底间隔 */
const DEFAULT_POLL_INTERVAL = 2000;

/** 从 pollWithStatus / stream finish 帧的 VO 中提取 token 统计，全空则返回 undefined */
export const pickFinalTokensFromVO = (res: ChatPollWithStatusVO | null | undefined): MessageTokens | undefined => {
  if (!res) return undefined;
  const { inputTokenCnt, completeTokenCnt, cacheTokenCnt, tokenCount } = res;
  const isMissing = (v: number | null | undefined) => v === null || v === undefined;
  if (isMissing(inputTokenCnt) && isMissing(completeTokenCnt) && isMissing(cacheTokenCnt) && isMissing(tokenCount)) {
    return undefined;
  }
  return {
    input: inputTokenCnt ?? undefined,
    complete: completeTokenCnt ?? undefined,
    cache: cacheTokenCnt ?? undefined,
    total: tokenCount ?? undefined
  };
};

/** thinking.tools 增量合并：按 id 去重后追加，无新增时保持原引用 */
export const mergeThinkingTools = (prev: ThinkingToolCall[], next: ThinkingToolCall[]): ThinkingToolCall[] => {
  if (!next.length) return prev;
  const existingIds = new Set(prev.map((t) => t.id));
  const toAppend = next.filter((t) => !existingIds.has(t.id));
  return toAppend.length ? [...prev, ...toAppend] : prev;
};

/** resume 阶段的「游标缓存失效」判别：命中后应降级为轮询兜底 */
export const isCacheExpiredError = (err: StreamError): boolean =>
  !!err.desc && (err.desc.includes('失效') || err.desc.includes('过期') || err.desc.includes('不存在'));

/** 取本轮最终结果消息（最后一条 type=3） */
export const pickLastResult = (msgs: ChatMessageVO[]): ChatMessageVO | undefined =>
  [...msgs].reverse().find((m) => m.type === 3);

/** 聚合一批消息里的 type=2：文本取最后一条，tools 全量累计 */
export const aggregateProcessMessages = (msgs: ChatMessageVO[]): { text: string; tools: ThinkingToolCall[] } | null => {
  const type2 = msgs.filter((m) => m.type === 2);
  if (!type2.length) return null;
  return {
    text: (type2[type2.length - 1].content as any)?.text || '',
    tools: type2.flatMap((m) => extractToolCalls((m.content as any)?.tools))
  };
};

/** 当前所处的传输阶段 */
export type ChatTurnPhase = 'send' | 'resume' | 'poll';

/** 一轮对话的终态结果 */
export interface ChatTurnResult {
  status: 'SUCCESS' | 'FAILED';
  /** SUCCESS 时的权威正文（最后一条 type=3 的 text）；无可用文本时为 undefined，调用方应保留流式已累积内容 */
  content?: string;
  documents?: DocumentRef[];
  tokens?: MessageTokens;
  questionData?: QuestionData;
  uuid?: string;
  projectId?: string;
}

export interface ChatTurnCallbacks {
  /** accept 帧：后端协商出的 uuid / projectId */
  onAccept?: (data: AgentStreamAccept) => void;
  /** 游标推进：uuid / projectId / lastEventId 三元组，调用方可持久化以支持刷新后续推 */
  onCursor?: (cursor: { uuid: string; projectId: string; lastEventId: string }) => void;
  /** text_chunk：可见正文增量，需累加 */
  onTextChunk?: (chunk: string) => void;
  /** thinking_chunk：思考文本增量，需累加 */
  onThinkingChunk?: (chunk: string) => void;
  /** reasoning 帧提取出的工具调用（已丢弃与 text_chunk 重复的 text） */
  onTools?: (tools: ThinkingToolCall[]) => void;
  /** 轮询兜底期间的进度：思考文本覆盖 + tools 增量 */
  onProgress?: (progress: { text: string; tools: ThinkingToolCall[] }) => void;
  /** 实体刷新信号（按 message.id 去重后派发） */
  onEntityAction?: (action: EntityAction) => void;
  /** 传输阶段切换：send → resume → poll */
  onPhaseChange?: (phase: ChatTurnPhase) => void;
  /** 终态：业务成功 / 失败（含网络无法兜底的场景） */
  onSettled?: (result: ChatTurnResult) => void;
  /** 需要 OIDC 授权：调用方负责回滚消息与跳转 */
  onOidcRequired?: () => void;
}

export interface ChatTurnController {
  /** 主动结束本轮：断流、停轮询，且不再触发任何回调 */
  abort: () => void;
}

export interface ChatTurnParams {
  /** 新一轮对话：走 sendStream。与 resume 二选一 */
  request?: ChatSendRequest;
  /**
   * 恢复已有轮次：跳过 sendStream，直接 resumeStream 续推（刷新后恢复场景）。
   * 与 request 二选一，同时传入时 resume 优先。
   */
  resume?: { uuid: string; projectId: string; lastEventId?: string };
  initialProjectId?: string;
  pollIntervalMs?: number;
}

/**
 * 跑完一轮 Agent 对话，内部负责三级降级：
 * `sendStream` → 网络中断续推 `resumeStream`（带 Last-Event-ID）→ 再断或游标失效降级 `pollWithStatus` 轮询。
 * 传 `resume` 时以 `resumeStream` 起步（页面刷新后恢复进行中的轮次），降级链路一致。
 * 帧语义与去重规则见 `apidocs/前端流式对话接入指南.md`：正文只由 text_chunk 累加，
 * reasoning 帧丢 text 只取 tools，finish 帧为权威终态快照。
 */
export const runChatTurn = (params: ChatTurnParams, cb: ChatTurnCallbacks): ChatTurnController => {
  const pollInterval = params.pollIntervalMs ?? DEFAULT_POLL_INTERVAL;
  let acceptedUuid = params.resume?.uuid || '';
  let acceptedProjectId = params.resume?.projectId || params.initialProjectId || '';
  let phase: ChatTurnPhase = 'send';
  let lastEventId = params.resume?.lastEventId || '';
  let finished = false;
  let disposed = false;
  /** result 帧快照：无 finish 帧场景兜底 */
  let latestResult: ChatMessageVO | null = null;
  let ctrl: AbortController | null = null;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  /** 已派发过的 entityAction 所属 message.id，跨 finish / poll 路径去重 */
  const processedActionIds = new Set<string>();

  const clearPoll = () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  };

  const closeStream = () => {
    if (ctrl) {
      ctrl.abort();
      ctrl = null;
    }
  };

  /** 记录游标并对外派发，供调用方持久化（刷新后据此续推） */
  const noteCursor = (id?: string) => {
    if (!id) return;
    lastEventId = id;
    if (acceptedUuid && acceptedProjectId) {
      cb.onCursor?.({ uuid: acceptedUuid, projectId: acceptedProjectId, lastEventId });
    }
  };

  const settle = (result: ChatTurnResult) => {
    if (disposed) return;
    finished = true;
    clearPoll();
    closeStream();
    cb.onSettled?.({ ...result, uuid: acceptedUuid || undefined, projectId: acceptedProjectId || undefined });
  };

  const settleFailed = (tokens?: MessageTokens) => settle({ status: 'FAILED', tokens });

  const consumeEntityActions = (msgs: ChatMessageVO[]) => {
    if (!cb.onEntityAction) return;
    for (const m of msgs) {
      const ea = m.entityAction;
      if (ea?.action === 'refresh' && !processedActionIds.has(m.id)) {
        processedActionIds.add(m.id);
        cb.onEntityAction(ea);
      }
    }
  };

  /** 由终态 VO 收敛为 ChatTurnResult */
  const settleByVO = (vo: ChatPollWithStatusVO) => {
    const msgs = vo?.messages ?? [];
    consumeEntityActions(msgs);
    const tokens = pickFinalTokensFromVO(vo);
    if (vo?.status === 'FAILED') {
      settleFailed(tokens);
      return;
    }
    const lastResult = pickLastResult(msgs);
    const questionMsg = msgs.find((m) => extractQuestionData(m.content));
    settle({
      status: 'SUCCESS',
      content: (lastResult?.content as any)?.text || undefined,
      documents: lastResult?.attachedDocuments,
      tokens,
      questionData: questionMsg ? extractQuestionData(questionMsg.content) : undefined
    });
  };

  const startPolling = () => {
    if (disposed || !acceptedUuid || !acceptedProjectId) {
      settleFailed();
      return;
    }
    clearPoll();
    closeStream();
    phase = 'poll';
    cb.onPhaseChange?.('poll');

    const tick = async () => {
      if (disposed) return;
      try {
        const res = await _chatPollWithStatus({ uuid: acceptedUuid, projectId: acceptedProjectId });
        if (disposed) return;
        const msgs = res?.messages ?? [];
        consumeEntityActions(msgs);
        const progress = aggregateProcessMessages(msgs);
        if (progress) cb.onProgress?.(progress);
        if (res?.status === 'IN_PROGRESS') {
          pollTimer = setTimeout(tick, pollInterval);
          return;
        }
        settleByVO(res);
      } catch {
        if (disposed) return;
        settleFailed();
      }
    };

    pollTimer = setTimeout(tick, pollInterval);
  };

  const handlers: StreamHandlers = {
    onAccept(data, meta) {
      acceptedUuid = data.uuid;
      acceptedProjectId = data.projectId;
      noteCursor(meta.id);
      cb.onAccept?.(data);
    },
    onTextChunk(chunk, meta) {
      noteCursor(meta.id);
      if (chunk) cb.onTextChunk?.(chunk);
    },
    onThinkingChunk(chunk, meta) {
      noteCursor(meta.id);
      if (chunk) cb.onThinkingChunk?.(chunk);
    },
    onReasoning(data, meta) {
      noteCursor(meta.id);
      // 去重协作：reasoning.content.text 与 text_chunk 累积的正文重复，此处只取结构化 tools
      const tools = extractToolCalls((data.content as any)?.tools);
      if (tools.length) cb.onTools?.(tools);
    },
    onToolResult(_data, meta) {
      noteCursor(meta.id);
      // tool_result 帧为 content.tool_result[]，工具名已由 reasoning 帧渲染，仅记录游标
    },
    onResult(data, meta) {
      noteCursor(meta.id);
      // finish 会覆盖，此处仅记录以备无 finish 帧场景兜底
      latestResult = data;
    },
    onFinish(data, meta) {
      noteCursor(meta.id);
      settleByVO(data);
    },
    onError(err: StreamError) {
      if (disposed) return;
      if (err.isOidcRequired) {
        finished = true;
        clearPoll();
        closeStream();
        cb.onOidcRequired?.();
        return;
      }
      // resume 阶段游标失效 → 降级轮询兜底
      if (phase === 'resume' && isCacheExpiredError(err) && acceptedUuid && acceptedProjectId) {
        startPolling();
        return;
      }
      // 其它业务错误：落失败，错误提示由调用方按需处理（禁止业务层重复弹 message）
      settleFailed();
    }
  };

  /** 流正常关闭但未收到 finish 帧：result 帧兜底 → 轮询兜底 → 落失败 */
  const onStreamClosed = () => {
    if (disposed || finished) return;
    if (latestResult) {
      settle({
        status: 'SUCCESS',
        content: (latestResult.content as any)?.text || undefined,
        documents: latestResult.attachedDocuments,
        questionData: extractQuestionData(latestResult.content)
      });
      return;
    }
    if (acceptedUuid && acceptedProjectId) {
      startPolling();
      return;
    }
    settleFailed();
  };

  const onStreamNetworkError = (currentPhase: ChatTurnPhase) => {
    if (disposed || finished) return;
    if (currentPhase === 'send') {
      // accept 未到达：无法 resume / poll → 落失败
      if (!acceptedUuid || !acceptedProjectId) {
        settleFailed();
        return;
      }
      tryResume();
      return;
    }
    // resume 阶段网络中断：不再自动 resume，直接轮询兜底
    startPolling();
  };

  const tryResume = () => {
    phase = 'resume';
    cb.onPhaseChange?.('resume');
    const next = new AbortController();
    ctrl = next;
    _chatResumeStream(
      { uuid: acceptedUuid, projectId: acceptedProjectId, lastEventId: lastEventId || undefined },
      handlers,
      next.signal
    )
      .then(onStreamClosed)
      .catch(() => onStreamNetworkError('resume'));
  };

  if (params.resume) {
    // 刷新恢复：跳过 sendStream，直接从游标续推（游标失效 → onError 内降级轮询）
    tryResume();
  } else if (params.request) {
    const first = new AbortController();
    ctrl = first;
    _chatSendStream(
      { ...params.request, projectId: params.request.projectId || params.initialProjectId || undefined },
      handlers,
      first.signal
    )
      .then(onStreamClosed)
      .catch(() => onStreamNetworkError('send'));
  } else {
    settleFailed();
  }

  return {
    abort: () => {
      disposed = true;
      clearPoll();
      closeStream();
    }
  };
};
